import { Button } from '@dev-spendesk/grapes';
import classnames from 'classnames';
import React, { type ReactNode, useState } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';

import { BetaFeatureFeedbackModal } from './BetaFeatureFeedbackModal';
import styles from './BetaFeatureFeedbackPopover.module.css';
import { BetaFeatureGetPreviousVersionModal } from './BetaFeatureGetPreviousVersionModal';
import { type EXTRA_FEATURES } from '../BetaFeatureNotice';
import { Popover } from '../Popover';
import { useBetaFeatureNoticeTracking } from '../hooks';

export type FeedbackModalVariant = 'default' | 'withRating';

type Props = {
  featureName: FEATURES | EXTRA_FEATURES;
  disableSwitchToPreviousVersion: boolean;
  feedbackTags: string[];
  title?: ReactNode;
  content?: ReactNode;
  buttonText?: string;
  modalVariant?: FeedbackModalVariant;
  isFeedbackMandatory: boolean;
  analyticsContext?: string;
  isForceAbsolute?: boolean;
  onClose(): void;
  onFeedbackSent?(): void;
};

export const BetaFeatureFeedbackPopover = ({
  featureName,
  disableSwitchToPreviousVersion,
  feedbackTags,
  title,
  content,
  buttonText,
  modalVariant,
  isFeedbackMandatory,
  analyticsContext,
  onClose,
  onFeedbackSent,
  isForceAbsolute,
}: Props) => {
  const { t } = useTranslation('global');
  const betaFeatureNoticeTracking = useBetaFeatureNoticeTracking({
    featureName,
  });
  const [isFeatureFeedbackModalOpen, setShowFeatureFeedbackModal] =
    useState(false);

  const [
    isBetaFeatureGetPreviousVersionModalOpen,
    setShowBetaFeatureGetPreviousVersionModal,
  ] = useState(false);

  const popoverTitle =
    title ?? `${t('betaFeature.feedbackNotification.title')} ✨`;
  const popoverContent =
    content ?? t('betaFeature.feedbackNotification.description');

  return (
    <>
      <Popover
        title={popoverTitle}
        content={popoverContent}
        actions={[
          ...(!disableSwitchToPreviousVersion
            ? [
                <Button
                  key="goToOldVersion"
                  onClick={() =>
                    setShowBetaFeatureGetPreviousVersionModal(true)
                  }
                  text={t(
                    'betaFeature.feedbackNotification.returnToPreviousVersion',
                  )}
                  variant="tertiaryNeutral"
                  className="text-content-complementary"
                />,
              ]
            : []),
          <Button
            key="giveFeedback"
            text={
              buttonText ?? t('betaFeature.feedbackNotification.giveFeedback')
            }
            variant="secondaryNeutral"
            onClick={() => setShowFeatureFeedbackModal(true)}
          />,
        ]}
        className={classnames(
          styles.BetaFeatureFeedbackPopover,
          isForceAbsolute &&
            styles['BetaFeatureFeedbackPopover--force-absolute'],
        )}
        onClose={onClose}
        disableClickAwayClose={
          isFeedbackMandatory ||
          isBetaFeatureGetPreviousVersionModalOpen ||
          isFeatureFeedbackModalOpen
        }
      />
      {!disableSwitchToPreviousVersion &&
        Object.values(FEATURES).includes(featureName as FEATURES) && (
          <BetaFeatureGetPreviousVersionModal
            featureName={featureName as FEATURES}
            isOpen={isBetaFeatureGetPreviousVersionModalOpen}
            onConfirm={() => {
              betaFeatureNoticeTracking.onSubmitFeedbackAndSwitchToOldVersionButtonClicked();
              onClose();
            }}
            onCancel={() => setShowBetaFeatureGetPreviousVersionModal(false)}
            feedbackTags={feedbackTags}
          />
        )}
      <BetaFeatureFeedbackModal
        isOpen={isFeatureFeedbackModalOpen}
        modalVariant={modalVariant}
        onCancel={() => setShowFeatureFeedbackModal(false)}
        onConfirm={() => {
          betaFeatureNoticeTracking.onSubmitFeedbackButtonClicked();
          if (onFeedbackSent) {
            onFeedbackSent();
          }
          onClose();
        }}
        feedbackTags={feedbackTags}
        analyticsContext={analyticsContext}
      />
    </>
  );
};
